import { FormEvent, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ApiError from "@/model/ApiError";
import PlaceRequest from "@/model/PlaceRequest";
import PlaceResponse from "@/model/PlaceResponse";
import "@/components/PlaceForm/PlaceForm.scss";

interface PlaceFormProps {
  title: string;
  submitLabel: string;
  initialPlace?: PlaceResponse;
  onSubmit: (request: PlaceRequest) => Promise<void>;
}

function PlaceForm({ title, submitLabel, initialPlace, onSubmit }: PlaceFormProps) {
  const { t } = useTranslation();

  const [name, setName] = useState(initialPlace?.name ?? "");
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      await onSubmit({ name: name.trim() });
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setError(t("places.form.conflictError"));
      } else if (err instanceof ApiError && err.status === 400) {
        setError(t("places.form.validationError"));
      } else if (err instanceof ApiError && err.status === 404) {
        setError(t("places.form.notFoundError"));
      } else {
        setError(t("places.form.genericError"));
      }
      setIsSubmitting(false);
    }
  };

  return (
    <form className="place-form" onSubmit={handleSubmit}>
      <h1>{title}</h1>

      <label htmlFor="name">{t("places.form.nameLabel")}</label>
      <input
        id="name"
        type="text"
        maxLength={100}
        value={name}
        onChange={(event) => setName(event.target.value)}
        required
      />
      {initialPlace && (
        <p className="place-form__help">{t("places.form.renameHelp")}</p>
      )}

      {error && <p className="place-form__error">{error}</p>}

      <div className="place-form__actions">
        <button type="submit" disabled={isSubmitting}>
          {isSubmitting ? t("places.form.submitting") : submitLabel}
        </button>
        <Link to="/places">{t("places.form.cancel")}</Link>
      </div>
    </form>
  );
}

export default PlaceForm;
export type { PlaceFormProps };
